import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useDroppable } from '@dnd-kit/core';
import { getArrayMapping, getLeafMapping, isArrayConfigured, isLeafConfigured } from '../../utils/fieldsTreeMutations';
import { buildQualifiedPath } from '../../utils/fieldCatalogUtils';
import { filterCatalogTree } from '../../utils/marminCatalogFilters';

const MarminDropTarget = ({ path, kind, required, configured, hasTransforms, children, disabled }) => {
  const dropId = kind === 'array' ? `marmin-array:${path}` : `marmin-leaf:${path}`;
  const { isOver, setNodeRef } = useDroppable({
    id: dropId,
    data: { type: kind === 'array' ? 'marmin-array' : 'marmin-leaf', path },
    disabled,
  });

  const showRequiredWarning = required && !configured;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderRadius: 1,
        border: '1px dashed',
        borderColor: isOver ? 'primary.main' : showRequiredWarning ? 'error.main' : 'divider',
        bgcolor: showRequiredWarning ? 'rgba(211, 47, 47, 0.25)' : isOver ? 'action.hover' : 'transparent',
        px: 1,
        py: 0.75,
        mb: 0.5,
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      {children}
      {hasTransforms && <Chip label="Has transforms" size="small" color="info" variant="outlined" sx={{ mt: 0.5 }} />}
    </Box>
  );
};

const CatalogNode = ({
  name,
  node,
  parentPath,
  fields,
  selectedErpPath,
  onMapErpToMarmin,
  onSetDefault,
  onClearLeaf,
  onClearArray,
  disabled,
  defaultExpanded,
}) => {
  const path = buildQualifiedPath(parentPath, name);
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (node.kind === 'field') {
    const mapping = getLeafMapping(fields, path);
    const configured = isLeafConfigured(mapping);

    return (
      <MarminDropTarget
        path={path}
        kind="field"
        required={node.required}
        configured={configured}
        hasTransforms={mapping.hasTransforms}
        disabled={disabled}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {path}
            </Typography>
            {mapping.source && (
              <Typography variant="caption" color="success.light" sx={{ fontFamily: 'monospace' }}>
                ← {mapping.source}
              </Typography>
            )}
          </Box>
          {node.required && <Chip label="Required" size="small" color={configured ? 'success' : 'error'} />}
          {node.sftpPathParty && (
            <Chip
              label="SFTP path"
              size="small"
              color="info"
              variant="outlined"
              title="Required on publish for SFTP XLSX routing"
            />
          )}
          {configured && (
            <Tooltip title="Clear mapping">
              <IconButton size="small" onClick={() => onClearLeaf(path)} disabled={disabled}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Default"
            value={
              mapping.defaultValue === undefined || mapping.defaultValue === null ? '' : String(mapping.defaultValue)
            }
            onChange={(event) => onSetDefault(path, event.target.value)}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <Box
            onClick={() => {
              if (selectedErpPath && !disabled) {
                onMapErpToMarmin(selectedErpPath, path, 'field');
              }
            }}
            sx={{
              alignSelf: 'center',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: selectedErpPath ? 'primary.main' : 'divider',
              cursor: selectedErpPath && !disabled ? 'pointer' : 'default',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="caption" color={selectedErpPath ? 'primary.main' : 'text.secondary'}>
              {selectedErpPath ? 'Click to map selection' : 'Drop ERP field here'}
            </Typography>
          </Box>
        </Stack>
      </MarminDropTarget>
    );
  }

  const childEntries = Object.entries(node.children || {});
  const isArray = node.kind === 'array';
  const arrayMapping = isArray ? getArrayMapping(fields, path) : null;
  const arrayConfigured = isArray ? isArrayConfigured(arrayMapping) : false;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      sx={{ mb: 0.5 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={node.kind} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {path}
          </Typography>
          {node.required && (
            <Chip label="Required" size="small" color={isArray ? (arrayConfigured ? 'success' : 'error') : 'warning'} />
          )}
          {node.sftpPathParty && (
            <Chip
              label="SFTP path"
              size="small"
              color="info"
              variant="outlined"
              title="Required on publish for SFTP XLSX routing"
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {isArray && (
          <MarminDropTarget
            path={path}
            kind="array"
            required={node.required}
            configured={arrayConfigured}
            hasTransforms={arrayMapping?.hasFilter}
            disabled={disabled}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Map an ERP array path first, then map item fields below using relative paths (e.g. $.name).
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Array source:{' '}
                <Typography component="span" sx={{ fontFamily: 'monospace' }}>
                  {arrayMapping?.source || 'not set'}
                </Typography>
              </Typography>
              {arrayConfigured && (
                <Tooltip title="Clear array source">
                  <IconButton size="small" onClick={() => onClearArray(path)} disabled={disabled}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Box
              onClick={() => {
                if (selectedErpPath && !disabled) {
                  onMapErpToMarmin(selectedErpPath, path, 'array');
                }
              }}
              sx={{
                mt: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selectedErpPath ? 'primary.main' : 'divider',
                cursor: selectedErpPath && !disabled ? 'pointer' : 'default',
              }}
            >
              <Typography variant="caption" color={selectedErpPath ? 'primary.main' : 'text.secondary'}>
                {selectedErpPath ? 'Click to map selection to array source' : 'Drop ERP array path here'}
              </Typography>
            </Box>
          </MarminDropTarget>
        )}

        {childEntries.map(([childName, childNode]) => (
          <CatalogNode
            key={buildQualifiedPath(path, childName)}
            name={childName}
            node={childNode}
            parentPath={path}
            fields={fields}
            selectedErpPath={selectedErpPath}
            onMapErpToMarmin={onMapErpToMarmin}
            onSetDefault={onSetDefault}
            onClearLeaf={onClearLeaf}
            onClearArray={onClearArray}
            disabled={disabled}
            defaultExpanded={childNode.required}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const MarminDropTree = ({
  fieldCatalog,
  fields,
  searchQuery,
  requiredOnly,
  selectedErpPath,
  onMapErpToMarmin,
  onSetDefault,
  onClearLeaf,
  onClearArray,
  disabled,
}) => {
  const filteredTree = useMemo(
    () => filterCatalogTree(fieldCatalog?.fields, searchQuery, requiredOnly),
    [fieldCatalog?.fields, searchQuery, requiredOnly],
  );

  if (!fieldCatalog?.fields) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading Marmin field catalog…
      </Typography>
    );
  }

  const entries = Object.entries(filteredTree);

  if (!entries.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No Marmin fields match your filters.
      </Typography>
    );
  }

  return (
    <Box>
      {entries.map(([name, node]) => (
        <CatalogNode
          key={name}
          name={name}
          node={node}
          parentPath=""
          fields={fields}
          selectedErpPath={selectedErpPath}
          onMapErpToMarmin={onMapErpToMarmin}
          onSetDefault={onSetDefault}
          onClearLeaf={onClearLeaf}
          onClearArray={onClearArray}
          disabled={disabled}
          defaultExpanded={node.required || requiredOnly}
        />
      ))}
    </Box>
  );
};

export default MarminDropTree;
